// External Dependencies:
import React from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router'
import auth0 from 'auth0-js'
import Cookie from 'js-cookie'
import axios from 'axios'

// Internal Dependencies:
import { getUtcTimestampSeconds } from 'utilities/time'
import { getStorage, setStorage } from 'utilities/storage'

// Redux:
import { getAccountData, receiveUserData, checkLoginNudgeState } from 'actions/site'
import { setScheduleUserData } from 'actions/schedule'

const clearCookies = () => {
	Cookie.remove( 'auth_access_token', { path: '/', domain: siteData.domain } )
	Cookie.remove( 'auth_refresh_token', { path: '/', domain: siteData.domain } )
	Cookie.remove( 'auth_expires_in', { path: '/', domain: siteData.domain } )
}

const clearLocalStorage = () => {
	localStorage.removeItem( 'auth_access_token' )
	localStorage.removeItem( 'auth_refresh_token' )
	localStorage.removeItem( 'auth_expires_at' )
}

export const getAuthToken = () => {
	// Local Storage:
	let localStorageToken = getStorage( 'auth_access_token' )

	if ( localStorageToken ) {
		return localStorageToken
	}

	// Cookie:
	let cookieToken = Cookie.get( 'auth_access_token' )

	if ( cookieToken ) {
		return cookieToken
	}

	// Not set:
	return ''
}

export const getRefreshToken = () => {
	// Local Storage:
	let localStorageToken = getStorage( 'auth_refresh_token' )

	if ( localStorageToken ) {
		return localStorageToken
	}

	// Cookie:
	let cookieToken = Cookie.get( 'auth_refresh_token' )

	if ( cookieToken ) {
		return cookieToken
	}

	// Not set:
	return ''
}

export const clearAuthData = () => {
	clearCookies()
	clearLocalStorage()
}

class Auth extends React.Component {
	constructor() {
		super()

		this.auth0 = new auth0.WebAuth( {
			domain: siteData.auth0_domain,
			clientID: siteData.auth0_client_id,
			redirectUri: siteData.auth0_redirect_url,
			audience: siteData.auth0_audience,
			responseType: 'code',
			responseMode: 'form_post',
			scope: 'openid profile email offline_access'
		} )
	}

	componentDidMount() {
		const { login, signup, logout } = this.props

		if ( login ) {
			this.login()

			return
		}

		if ( signup ) {
			this.signup()

			return
		}

		if ( logout ) {
			this.logout()

			return
		}

		let localStorageToken = getStorage( 'auth_access_token' )

		if ( localStorageToken ) {
			if ( this.isAuthenticated() ) {
				// Start session with existing local storage token:
				this.getUserData()
			} else {
				// Use the refresh token to renew the session:
				this.renewSession()
			}
		} else {
			// Check for cookies:
			let cookies = this.getCookies()

			// Set local storage data, then start session:
			if ( cookies.cookieToken && cookies.cookieRefreshToken && cookies.cookieExpires ) {
				this.setSession( cookies.cookieToken, cookies.cookieRefreshToken, cookies.cookieExpires )
				this.getUserData()
			} else {
				this.props.dispatch( receiveUserData( {} ) )
				this.props.dispatch( checkLoginNudgeState() )
			}
		}
	}

	getCookies = () => {
		// Cookie:
		let cookieToken = Cookie.get( 'auth_access_token' )
		let cookieRefreshToken = Cookie.get( 'auth_refresh_token' )
		let cookieExpires = Cookie.get( 'auth_expires_in' )

		return {
			cookieToken,
			cookieRefreshToken,
			cookieExpires
		}
	}

	setSession = ( accessToken, refreshToken, expiresIn ) => {
		// Set the time that the access token will expire at:
		let expiresAt = JSON.stringify( ( expiresIn * 1000 ) + new Date().getTime() )

		setStorage( 'auth_has_logged_in_before', '1' )
		setStorage( 'auth_has_logged_out_manually', '' )
		setStorage( 'auth_has_logged_out_manually_timestamp', '' )
		setStorage( 'auth_access_token', accessToken )
		setStorage( 'auth_refresh_token', refreshToken )
		setStorage( 'auth_expires_at', expiresAt )

		// Clear access token and 'expires in' cookies:
		clearCookies()
	}

	renewSession = ( ) => {
		const refreshToken = getRefreshToken()

		axios( {
			url: siteData.api_base_url + 'refresh-user',
			timeout: 20000,
			method: 'POST',
			responseType: 'json',
			data: {
				auth_refresh_token: refreshToken
			}
		} )
		.then( response => {
			if ( response.data && response.data.access_token && response.data.refresh_token ) {
				this.setSession( response.data.access_token, response.data.refresh_token, response.data.expires_in )

				this.getUserData()
			}
		} )
		.catch( error => {
			if ( error.response ) {
				console.error( error.response, 'error.response' )
			}
		} )
	}

	isAuthenticated = () => {
		// Check whether the current time is past the access token's expiry time:
		let expiresAt = JSON.parse( getStorage( 'auth_expires_at' ) )

		if ( new Date().getTime() < expiresAt ) {
			return true
		}

		// If there is a token and expires cookie, we're probably about to set the session,
		// but haven't yet. Allow access.
		let cookieToken = Cookie.get( 'auth_access_token' )
		let cookieExpires = Cookie.get( 'auth_expires_in' )

		if ( cookieToken && cookieExpires ) {
			return true
		}

		return false
	}

	login = () => {
		this.auth0.authorize( {
			redirectUri: `${siteData.auth0_redirect_url}?redirect_to=${document.referrer}`
		} )
	}

	signup = () => {
		this.auth0.authorize( {
			redirectUri: `${siteData.auth0_redirect_url}?redirect_to=${document.referrer}`,
			form_type: 'signup'
		} )
	}

	logout = () => {
		const { dispatch, history } = this.props

		// Clear access token and expiration from local storage:
		clearAuthData()
		dispatch( receiveUserData( {} ) )
		dispatch( setScheduleUserData( {} ) )

		this.auth0.logout( {
			federated: true
		} )

		setStorage( 'auth_has_logged_out_manually', '1' )
		setStorage( 'auth_has_logged_out_manually_timestamp', getUtcTimestampSeconds() )

		history.push( '/' )
	}

	getUserData = () => {
		this.props.dispatch( getAccountData() )
	}

	render() {
		return null
	}
}

const mapStateToProps = ( state, ownProps ) => {
	return {

	}
}

export default withRouter(
	connect( mapStateToProps )( Auth )
)
